/**
 * The page shell (§A.2/requirement 5): the fleet bar (organism dots + nav),
 * the routed content area (organism screen | family screen | fleet
 * screen), dark theme, responsive layout — all wired here.
 *
 * Nav/organism routing is driven by `fleet.json` when it is published
 * (owner requirement 2026-09-26: "drive the fleet screen and the organism
 * routes from fleet.json"), falling back to `src/config.ts`'s
 * `defaultRoles` otherwise (`src/data/roleDirectory.ts`).
 */

import { Router, routeToHash, type Route } from "../router/router";
import { loadFleetOnce, resolveAllRoleLocations, resolveRoleLocation } from "../data/roleDirectory";
import { OrganismScreen } from "./OrganismScreen";
import { FamilyScreen } from "./FamilyScreen";
import { BenchmarksScreen } from "./BenchmarksScreen";
import { FleetScreen } from "./FleetScreen";
import { attachTooltip } from "../ui/tooltip";
import { fleetStatus } from "../data/fleetStatus";

interface Screen {
  mount(): Promise<void>;
  dispose(): void;
}

export class App {
  private readonly router = new Router();
  private readonly navLinks = new Map<string, HTMLAnchorElement>();
  private currentScreen: Screen | null = null;
  private contentEl!: HTMLDivElement;
  private navEl!: HTMLElement;
  private mountToken = 0;

  constructor(private readonly root: HTMLElement) {}

  /** Exposed for the Playwright smoke test only (via `window.__obsApp`,
   * `main.ts`) — never read by any panel/screen itself. */
  get currentOrganismScreen(): OrganismScreen | null {
    return this.currentScreen instanceof OrganismScreen ? this.currentScreen : null;
  }

  mount(): void {
    this.root.innerHTML = "";
    this.root.className = "obs-app";

    // One organism list, not two (owner requirement 2026-09-27: the fleet
    // bar previously showed "Male · equities / Female · crypto" twice — a
    // left dot+label chip and, right next to it, the identical clickable
    // nav link). The nav is the functional one (routes, active-highlight);
    // each organism's colour now lives as a small dot *inside* its own nav
    // link instead of a redundant standalone chip.
    const fleetBar = document.createElement("div");
    fleetBar.className = "obs-fleetbar";

    // A link back to the main Futarchists site (owner requirement
    // 2026-10-01: the Observatory and the main site must link to each
    // other), using the same mark as futarchists.xyz's own favicon/brand so
    // the pairing reads as one product, not two unrelated pages. Sits to
    // the left of — and visually "parents" — the product wordmark below,
    // like a breadcrumb: "Futarchists / Fly Observatory".
    const homeLink = document.createElement("a");
    homeLink.className = "obs-home-link";
    homeLink.href = "https://futarchists.xyz/";
    attachTooltip(
      homeLink,
      "Futarchists — the autonomous quant research desk this observatory is part of.",
    );
    const homeLogo = document.createElement("img");
    homeLogo.className = "obs-home-logo";
    // import.meta.env.BASE_URL (not a hardcoded "/"), same pattern as
    // src/sw/registerServiceWorker.ts, so this resolves wherever the
    // Futarchists site mounts this bundle (VITE_BASE_PATH, default
    // "/observatory/").
    homeLogo.src = `${import.meta.env.BASE_URL}logo.svg`;
    homeLogo.alt = "";
    homeLogo.width = 16;
    homeLogo.height = 16;
    homeLink.appendChild(homeLogo);
    homeLink.appendChild(document.createTextNode("Futarchists"));
    fleetBar.appendChild(homeLink);

    const crumbSep = document.createElement("span");
    crumbSep.className = "obs-crumb-sep";
    crumbSep.setAttribute("aria-hidden", "true");
    crumbSep.textContent = "/";
    fleetBar.appendChild(crumbSep);

    // A product wordmark, not just a bare nav row (orchestrator course
    // correction, 2026-09-28: "add a product wordmark on the left").
    const wordmark = document.createElement("a");
    wordmark.className = "obs-wordmark";
    wordmark.href = routeToHash({ kind: "fleet" });
    wordmark.textContent = "Fly Observatory";
    wordmark.addEventListener("click", (event) => {
      event.preventDefault();
      this.router.navigate({ kind: "fleet" });
    });
    fleetBar.appendChild(wordmark);

    this.navEl = document.createElement("nav");
    attachTooltip(
      this.addNavLink(this.navEl, { kind: "fleet" }, "Fleet"),
      "Every known organism side by side — NAV, stage, drawdown, heartbeat.",
    );
    attachTooltip(
      this.addNavLink(this.navEl, { kind: "benchmarks" }, "Benchmarks"),
      "Each fly vs equal-weight buy-and-hold, core-only and cash since its episode's genesis — " +
        "returns, drawdown, costs, consistency, regimes and the rolling 30-day gate.",
    );
    attachTooltip(
      this.addNavLink(this.navEl, { kind: "family" }, "Family"),
      "Procreation readiness, odds, and the family tree across parents.",
    );
    fleetBar.appendChild(this.navEl);

    this.contentEl = document.createElement("div");
    this.contentEl.className = "obs-content";

    this.root.appendChild(fleetBar);
    this.root.appendChild(this.contentEl);

    void this.populateOrganismNav();
    this.router.subscribe((route) => void this.showRoute(route));
  }

  /** Populates the organism dots + nav links once role locations are known
   * (either from `fleet.json` or the config fallback) — async because
   * discovering them may require one network round trip. Each pill also
   * gets a live micro-status dot (design/revamp/DIRECTION.md §3) sourced
   * from `fleet.json`'s heartbeat-derived status
   * (`src/data/fleetStatus.ts`, shared with the fleet screen's own cards)
   * — omitted entirely when `fleet.json` isn't published, never guessed. */
  private async populateOrganismNav(): Promise<void> {
    // `loadFleetOnce()` (not `loadFleetSnapshot()` directly): shares the
    // exact same in-flight/resolved fleet.json promise `resolveAllRoleLocations()`
    // uses internally, instead of issuing a second concurrent fetch for the
    // same URL (code-review finding, revamp self-review round 1).
    const [locations, fleet] = await Promise.all([resolveAllRoleLocations(), loadFleetOnce()]);
    const now = Date.now();
    for (const location of locations) {
      const organismRoute: Route = { kind: "organism", role: location.role };
      const link = this.addNavLink(
        this.navEl,
        organismRoute,
        location.displayName,
        this.navEl.firstChild,
      );
      const dot = document.createElement("span");
      dot.className = "obs-org-dot";
      dot.style.background =
        location.role === "male_equity" ? "var(--obs-male)" : "var(--obs-female)";
      link.prepend(dot);

      const fleetEntry = fleet?.organisms.find((o) => o.role === location.role) ?? null;
      let tooltip = `Brain, market view, log, portfolio, life, and family for ${location.displayName}.`;
      if (fleetEntry) {
        const status = fleetStatus(fleetEntry, now);
        const statusDot = document.createElement("span");
        statusDot.className = `obs-switcher-status ${status.dotClass}`;
        link.appendChild(statusDot);
        tooltip += ` ${status.tooltip}`;
      }
      attachTooltip(link, tooltip);
      // Re-apply highlight in case this link was added after the initial route resolved.
      link.classList.toggle(
        "obs-active",
        routeToHash(this.router.current) === routeToHash(organismRoute),
      );
    }
  }

  private addNavLink(
    nav: HTMLElement,
    route: Route,
    label: string,
    before: ChildNode | null = null,
  ): HTMLAnchorElement {
    const link = document.createElement("a");
    link.href = routeToHash(route);
    link.textContent = label;
    link.addEventListener("click", (event) => {
      event.preventDefault();
      this.router.navigate(route);
    });
    if (before) nav.insertBefore(link, before.nextSibling);
    else nav.appendChild(link);
    this.navLinks.set(routeToHash(route), link);
    return link;
  }

  private highlightNav(route: Route): void {
    for (const [hash, link] of this.navLinks) {
      link.classList.toggle("obs-active", hash === routeToHash(route));
    }
  }

  private async showRoute(route: Route): Promise<void> {
    const token = ++this.mountToken;
    this.highlightNav(route);
    this.currentScreen?.dispose();
    this.currentScreen = null;
    this.contentEl.innerHTML = "";

    let screen: Screen;
    if (route.kind === "fleet") {
      screen = new FleetScreen(this.contentEl, this.router);
    } else if (route.kind === "family") {
      screen = new FamilyScreen(this.contentEl);
    } else if (route.kind === "benchmarks") {
      screen = new BenchmarksScreen(this.contentEl, this.router);
    } else {
      const location = await resolveRoleLocation(route.role);
      screen = new OrganismScreen(this.contentEl, location);
    }
    if (token !== this.mountToken) return; // a newer navigation started while resolving
    this.currentScreen = screen;
    await screen.mount();
    if (token !== this.mountToken) {
      // A newer navigation happened while this one was loading; discard.
      screen.dispose();
    }
  }

  dispose(): void {
    this.currentScreen?.dispose();
    this.router.dispose();
  }
}
